import { FormsEntities } from '@bimser/common';
import { extend, IDictionary } from '@bimser/core';
import { IPropertyField, MultiLanguageInputText } from '../../../entities/common';
import { IUserEvent } from '../../../entities/services';
import {
	IControlExecutedServerEvent,
	IPropertyChangedEventArgs,
	IPropertyChangingEventArgs
} from '../entities/EventArgs';
import { IMultiLanguageTextControlProperties } from '../entities/IMultiLanguageTextControlProperties';
import { getCultureEnumMembers } from '../helper';
import TextControl from './TextControl';
import { JSX } from 'react';
import { getMLText } from '../../../helper';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';

export default class MultiLanguageTextControl extends TextControl {
	enableMultiLanguageText: boolean;
	_multiLanguageText: IDictionary<string> = {};
	multiLanguageText: MultiLanguageInputText;
	showCharacterCounter: boolean;

	constructor() {
		super();

		this.__triggerChange = this.__triggerChange.bind(this);
		this.__excludeChangingProperties = ['text', 'value'];
		this.__propertyMapping = { _multiLanguageText: 'multiLanguageText' };
		this.enableMultiLanguageText = false;
		this.showCharacterCounter = false;

		this.subscribe(
			'system:PropertyChanging:multiLanguageText',
			(eventArgs: FormsEntities.IPropertyChangeEventArgs) =>
				new Promise<void>((resolve, reject) => {
					const culture = eventArgs.dataField[1];
					if (eventArgs.value !== this._multiLanguageText[culture]) {
						const newMultiLanguageText = new MultiLanguageInputText(
							null,
							{ ...this._multiLanguageText, [culture]: eventArgs.value },
							this.enableMultiLanguageText
						);
						const oldMultiLanguageText = new MultiLanguageInputText(
							null,
							this._multiLanguageText,
							this.enableMultiLanguageText
						);

						const newText = newMultiLanguageText.getText(culture, true);
						const oldText = this.multiLanguageText.getText(culture, true);

						if (oldText !== newText) {
							const path = eventArgs.dataField.join('.');
							const propertyChangeArgs = {
								name: path,
								newValue: eventArgs.value,
								oldValue: _get(this, path),
								cancel: false
							};

							// TRIGGER PROPERTY CHANGING CLIENT EVENT
							this.emit<IPropertyChangingEventArgs<any>>(
								'event:OnPropertyChanging',
								propertyChangeArgs
							)
								.then(() => {
									if (!propertyChangeArgs.cancel) {
										const mlArgument = {
											name: this.name,
											newValue: newMultiLanguageText,
											oldValue: oldMultiLanguageText,
											cancel: false
										};

										// TRIGGER ML CHANGING CLIENT EVENT
										this.emit<
											IPropertyChangingEventArgs<MultiLanguageInputText>
										>('event:OnMultiLanguageTextChanging', mlArgument)
											.then(() => {
												if (!mlArgument.cancel) {
													// TRIGGER ML CHANGING SERVER EVENT
													this.emit<
														IUserEvent<
															IPropertyChangingEventArgs<
																IDictionary<string>
															>
														>
													>('event:server:OnMultiLanguageTextChanging', {
														argument: {
															...mlArgument,
															newValue:
																newMultiLanguageText.getData(),
															oldValue: oldMultiLanguageText.getData()
														},
														controlId: this.name,
														name: 'MultiLanguageTextChanging',
														sender: 'web'
													})
														.then(args => {
															const executedServerEvent: IControlExecutedServerEvent =
																args[0]?.executedServerEvent;
															if (
																!executedServerEvent ||
																!executedServerEvent?.parameters
																	.cancel
															) {
																const textArgument = {
																	name: this.name,
																	newValue: newText,
																	oldValue: oldText,
																	cancel: false
																};

																// TRIGGER TEXT CHANGING CLIENT EVENT
																this.emit<
																	IPropertyChangingEventArgs<string>
																>(
																	'event:OnTextChanging',
																	textArgument
																)
																	.then(() => {
																		if (!textArgument.cancel) {
																			// TRIGGER TEXT CHANGING SERVER EVENT
																			this.emit<
																				IUserEvent<
																					IPropertyChangingEventArgs<string>
																				>
																			>(
																				'event:server:OnTextChanging',
																				{
																					argument:
																						textArgument,
																					controlId:
																						this.name,
																					name: 'TextChanging',
																					sender: 'web'
																				}
																			)
																				.then(args => {
																					const executedServerEvent: IControlExecutedServerEvent =
																						args[0]
																							?.executedServerEvent;
																					if (
																						!executedServerEvent ||
																						!executedServerEvent
																							?.parameters
																							.cancel
																					) {
																						const valueArgument =
																							{
																								name: this
																									.name,
																								newValue:
																									newText,
																								oldValue:
																									oldText,
																								cancel: false
																							};

																						// TRIGGER VALUE CHANGING CLIENT EVENT
																						this.emit<
																							IPropertyChangingEventArgs<string>
																						>(
																							'event:OnValueChanging',
																							valueArgument
																						)
																							.then(
																								() => {
																									if (
																										!valueArgument.cancel
																									) {
																										// TRIGGER VALUE CHANGING SERVER EVENT
																										this.emit<
																											IUserEvent<
																												IPropertyChangingEventArgs<string>
																											>
																										>(
																											'event:server:OnValueChanging',
																											{
																												argument:
																													valueArgument,
																												controlId:
																													this
																														.name,
																												name: 'ValueChanging',
																												sender: 'web'
																											}
																										)
																											.then(
																												args => {
																													const executedServerEvent: IControlExecutedServerEvent =
																														args[0]
																															?.executedServerEvent;
																													if (
																														!executedServerEvent ||
																														!executedServerEvent
																															?.parameters
																															.cancel
																													) {
																														resolve(); // RESOLVE FINALLY
																													} else {
																														reject();
																													}
																												}
																											)
																											.catch(
																												() => {
																													reject();
																												}
																											);
																									} else {
																										reject();
																									}
																								}
																							)
																							.catch(
																								() => {
																									reject();
																								}
																							);
																					} else {
																						reject();
																					}
																				})
																				.catch(() => {
																					reject();
																				});
																		} else {
																			reject();
																		}
																	})
																	.catch(() => {
																		reject();
																	});
															} else {
																reject();
															}
														})
														.catch(() => {
															reject();
														});
												} else {
													reject();
												}
											})
											.catch(() => {
												reject();
											});
									} else {
										reject();
									}
								})
								.catch(() => {
									reject();
								});
						} else {
							reject();
						}
					} else {
						reject();
					}
				})
		);
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'multiLanguageText'];
	}

	initialize() {
		super.initialize();

		this.multiLanguageText = new MultiLanguageInputText(
			this.__triggerChange,
			{},
			this.enableMultiLanguageText,
			this.__form.currentLanguage.culture,
			this.__form.supportedLanguages.find(i => i.isDefault)?.culture
		);
	}

	get text(): string {
		if (this.multiLanguageText) {
			const text = this.multiLanguageText.getText(this.__form.currentLanguage.culture, true);

			if (typeof text === 'string') {
				return text;
			}
		}

		return '';
	}

	set text(text: string) {
		const culture = this.enableMultiLanguageText
			? this.__form.currentLanguage.culture
			: 'system';

		if (
			this.__form &&
			this.text !== text &&
			this._multiLanguageText[culture] !== text &&
			this.multiLanguageText
		) {
			this.multiLanguageText.setText(text);
		}
	}

	get value(): string {
		return this.multiLanguageText
			? this.multiLanguageText.getText(this.__form.currentLanguage.culture, true)
			: undefined;
	}

	set value(value: string) {
		const culture = this.enableMultiLanguageText
			? this.__form.currentLanguage.culture
			: 'system';

		if (
			this.__form &&
			this.value !== value &&
			this._multiLanguageText[culture] !== value &&
			this.multiLanguageText
		) {
			this.multiLanguageText.setText(value);
		}
	}

	__triggerChange(culture: string, text: string) {
		if (this._multiLanguageText[culture] !== text) {
			this.__proxyChanging._multiLanguageText[culture] = text;
		}
	}

	__loadProperties(data: IMultiLanguageTextControlProperties) {
		const oldMultiLanguageText = this.multiLanguageText.getData();
		const oldText = this.text;

		const defaultCulture = this.__form?.supportedLanguages.find(i => i.isDefault)?.culture;
		const userCulture = this.__form?.currentLanguage.culture;

		if (data.multiLanguageText) {
			const mlText = getMLText({
				culture: userCulture,
				dontForce: true,
				enableMultiLanguageText: this.enableMultiLanguageText,
				data: data.multiLanguageText,
				userCulture,
				defaultCulture
			});

			data.text = mlText;
			data.value = mlText;
		}

		super.__loadProperties(data);

		this.__proxyChanged.enableMultiLanguageText = data.enableMultiLanguageText;
		this.__proxyChanged.showCharacterCounter = data.showCharacterCounter;
		this.multiLanguageText.setEnableMultiLanguageText(data.enableMultiLanguageText);

		if (data.multiLanguageText) {
			this._multiLanguageText = data.multiLanguageText;
			this.multiLanguageText.setData(data.multiLanguageText);
		}

		const newMultiLanguageText = this.multiLanguageText.getData();

		if (!_isEqual(newMultiLanguageText, oldMultiLanguageText) && this.__isInitialized) {
			const newValue = new MultiLanguageInputText(
				null,
				newMultiLanguageText,
				this.enableMultiLanguageText,
				userCulture,
				defaultCulture
			);
			const oldValue = new MultiLanguageInputText(
				null,
				oldMultiLanguageText,
				this.enableMultiLanguageText,
				userCulture,
				defaultCulture
			);

			const argument = {
				name: this.name,
				newValue,
				oldValue
			};

			this.emit<IPropertyChangedEventArgs<MultiLanguageInputText>>(
				'event:OnMultiLanguageTextChanged',
				argument
			);

			const serverArgument = extend(true, {}, argument, {
				newValue: argument.newValue?.getData(),
				oldValue: argument.oldValue?.getData()
			});

			this.emit<IUserEvent<IPropertyChangedEventArgs<MultiLanguageInputText>>>(
				'event:server:OnMultiLanguageTextChanged',
				{
					argument: serverArgument,
					controlId: this.name,
					name: 'MultiLanguageTextChanged',
					sender: 'web'
				}
			);

			const propertyChangedArgs: IPropertyChangedEventArgs<any> = {
				name: 'multiLanguageText',
				oldValue: oldMultiLanguageText,
				newValue: this.multiLanguageText.getData(),
				controlName: this.name
			};

			this.emit('event:OnPropertyChanged', propertyChangedArgs);
		}

		const newText = this.text;

		if (oldText !== newText && this.__isInitialized) {
			const argumentText = {
				name: this.name,
				newValue: newText,
				oldValue: oldText
			};

			this.emit<IPropertyChangedEventArgs<string>>('event:OnTextChanged', argumentText);
			this.emit<IUserEvent<IPropertyChangedEventArgs<string>>>('event:server:OnTextChanged', {
				argument: argumentText,
				controlId: this.name,
				name: 'TextChanged',
				sender: 'web'
			});

			this.emit('event:OnPropertyChanged', {
				name: 'text',
				oldValue: oldText,
				newValue: this.text,
				controlName: this.name
			} as IPropertyChangedEventArgs<string>);

			const argumentValue = {
				name: this.name,
				newValue: newText,
				oldValue: oldText
			};

			this.emit<IPropertyChangedEventArgs<string>>('event:OnValueChanged', argumentValue);
			this.emit<IUserEvent<IPropertyChangedEventArgs<string>>>(
				'event:server:OnValueChanged',
				{
					argument: argumentValue,
					controlId: this.name,
					name: 'ValueChanged',
					sender: 'web'
				}
			);

			this.emit('event:OnPropertyChanged', {
				name: 'value',
				oldValue: oldText,
				newValue: this.value,
				controlName: this.name
			} as IPropertyChangedEventArgs<string>);
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			enableMultiLanguageText: this.enableMultiLanguageText,
			multiLanguageText: this.multiLanguageText.getData(),
			showCharacterCounter: this.showCharacterCounter
		};
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();

		let enableMultiLanguageText: boolean = this.enableMultiLanguageText;
		if (properties) {
			enableMultiLanguageText = properties.get('enableMultiLanguageText');
		}

		return [
			...fields,
			{
				name: 'enableMultiLanguageText',
				type: 'boolean'
			},
			{
				name: 'multiLanguageText',
				type: 'object',
				items: getCultureEnumMembers(enableMultiLanguageText)
			},
			{
				name: 'showCharacterCounter',
				type: 'boolean'
			}
		];
	}

	__render(children?: React.ReactNode): JSX.Element {
		return super.__render(children);
	}
}
